// #72 (SEC-114/SEC-115): the backend mints a 15-minute access token plus a
// 30-day rotating refresh token, but nothing in this app ever calls
// POST /users/refresh — SEC-027's proactive expiry check just logged the
// user out, so every tab was force-logged-out every 15 minutes of activity
// instead of the session silently renewing.
//
// One module, two interceptors, one single-flight refresh:
//
// - Request interceptor (proactive, was SEC-027's whole job): if the request
//   carries the stored token and it's already expired by the client's clock,
//   refresh before sending rather than rejecting outright.
// - Response interceptor (reactive, new): a safety net for a 401 the
//   proactive check didn't catch — clock skew, or the token was revoked
//   server-side between checks (a password change from another tab bumps
//   tokenVersion and kills every other session). Same refresh, same retry.
//
// Both share ONE in-flight refresh promise. The backend treats a second,
// concurrent presentation of the same refresh token as evidence of a replay
// and evicts the whole session family — so this is a correctness
// requirement, not an optimization: two triggers racing to refresh
// independently would log the user out for real.
//
// Every service function in this repo shares the one global `axios` module —
// there is no centralized instance (CQ-026) — so, as SEC-027 established,
// this is the one hook point that reaches all of them without editing every
// call site: each one re-reads the token from localStorage fresh at call
// time, so writing the refreshed pair back there is enough for every
// subsequent call, and rewriting the in-flight request's own header covers
// the one already built before the refresh landed.
import axios from "axios";
import BASE_URL from "./constants";
import { readCurrentUser } from "./currentUser";
import { clearSession } from "./session";
import { isTokenExpired, decodeJwtPayload } from "./tokenExpiry";
import { SESSION_EXPIRED_MESSAGE, SESSION_REFRESHED_EVENT, SESSION_REFRESH_REQUESTED_EVENT } from "./sessionEvents";

const REFRESH_URL = `${BASE_URL}/users/refresh`;
const REFRESH_LOCK_NAME = "astra-session-refresh";

// Thrown when a refresh attempt is no longer relevant by the time it can
// check — the signed-in account changed (a login/logout, here or in another
// tab) while this attempt was queued or in flight. Distinct from an actual
// refresh failure: the caller must not log anyone out for it, since a
// perfectly valid *different* session may already be active.
class StaleRefreshError extends Error {}

let refreshPromise = null;

// #87 / SEC-085: tells the socket (and anything else listening) that
// localStorage now holds a fresh pair. Dispatched once per settled refresh,
// not once per waiting caller, because it is attached where the shared
// promise is created.
function announceRefreshed(updated) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SESSION_REFRESHED_EVENT));
  }
  return updated;
}

// Serializes the callback across every tab sharing this origin, not just
// this one — required, not cosmetic. localStorage's refresh token is shared
// by every open tab, and the backend treats a second, concurrent
// presentation of the SAME refresh token as a replay and evicts the whole
// session family. A module-local promise only ever coordinated callers
// within one JS context; two tabs each idle past the access token's expiry
// would each start their own "single-flight" refresh with the same
// credential and both lose. The Web Locks API is the standard mechanism for
// this and has been in every evergreen browser since 2022 (Chrome 69,
// Firefox 96, Safari 15.4); where it's unavailable this degrades to
// same-tab-only coordination, same as before — a documented gap, not a
// silent one.
function withCrossTabLock(fn) {
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK_NAME, fn);
  }
  return fn();
}

// Exchanges the stored refresh token for a fresh {token, refreshToken} pair
// and persists it. Concurrent callers *in this tab* share the same promise;
// callers in *other* tabs wait on the cross-tab lock instead of also calling
// the network. Throws StaleRefreshError if the signed-in account changes
// while this call is queued or in flight (a login/logout elsewhere) — the
// account this refresh was FOR is captured once, up front, and checked
// again after the lock and after the network round trip, so a delayed
// response can never overwrite or clear a different, newer session that has
// since taken its place. Throws a plain Error for a genuine refresh failure
// (invalid, expired, or revoked token, or none stored at all).
function refreshSession() {
  if (refreshPromise) {
    return refreshPromise;
  }

  const initialStored = readCurrentUser();
  const accountId = decodeJwtPayload(initialStored?.token)?.userId;
  const tokenAtStart = initialStored?.token;

  refreshPromise = withCrossTabLock(async () => {
    // Now holding the lock (or running immediately if locks aren't
    // available): a sibling — in this tab or another — may have already
    // refreshed OUR account while we waited. Detected by comparing the
    // TOKEN, not by checking isTokenExpired: the reactive (response)
    // interceptor calls this for a token that isn't expired by the client's
    // clock at all — the server rejected it anyway (revoked elsewhere) —
    // and treating "not expired" as "already refreshed" would skip the
    // network call this caller specifically needs. Only a token that has
    // actually changed since we started means someone else already did the
    // work; use their result instead of presenting the (now-rotated-away)
    // refresh token a second time.
    const afterLock = readCurrentUser();
    const afterLockId = decodeJwtPayload(afterLock?.token)?.userId;
    if (!afterLockId || afterLockId !== accountId) {
      throw new StaleRefreshError("Session changed before refresh");
    }
    if (afterLock.token !== tokenAtStart) {
      return afterLock;
    }
    if (!afterLock.refreshToken) {
      throw new Error("No refresh token stored");
    }

    // True once this refresh's account/session is no longer the active one.
    // Checked wherever the network call can settle — success AND failure —
    // not only on success: axios rejects outright for a non-2xx response, so
    // an old, meanwhile-superseded session's refresh token coming back
    // invalid must still be recognized as stale, not treated as a genuine
    // failure of whatever session happens to be active by the time it
    // rejects. userId alone isn't enough either: the SAME account logging
    // out and back in while this refresh was in flight is still "userId
    // unchanged" but is a genuinely different session with its own refresh
    // token — and since this call holds the cross-tab lock, nothing but a
    // fresh login could have rotated `afterLock.refreshToken` in the
    // meantime, so comparing it catches that case too.
    const hasDrifted = () => {
      const current = readCurrentUser();
      const currentId = decodeJwtPayload(current?.token)?.userId;
      return currentId !== accountId || current?.refreshToken !== afterLock.refreshToken;
    };

    let res;
    try {
      res = await axios.post(REFRESH_URL, { refreshToken: afterLock.refreshToken });
    } catch (networkErr) {
      if (hasDrifted()) {
        throw new StaleRefreshError("Session changed during refresh");
      }
      throw networkErr;
    }

    if (hasDrifted()) {
      throw new StaleRefreshError("Session changed during refresh");
    }
    if (res?.status !== 200 || !res?.data?.token || !res?.data?.refreshToken) {
      throw new Error("Refresh failed");
    }

    const current = readCurrentUser();
    const updated = { ...current, token: res.data.token, refreshToken: res.data.refreshToken };
    localStorage.setItem("currentUser", JSON.stringify(updated));
    return updated;
  }).then(announceRefreshed);

  // Cleared whether it succeeds or fails, so the next expiry/401 starts a
  // fresh attempt rather than replaying a settled promise.
  return refreshPromise.finally(() => {
    refreshPromise = null;
  });
}

// A single failed refresh is shared by every request that was waiting on it
// (single-flight), and each one independently reaches this on the same
// tick — a dozen concurrent mount-time requests is ordinary. Without the
// already-cleared guard, all of them would call clearSession() and
// window.location.assign('/login') again, back to back. Both are idempotent
// in effect, but not free: repeated navigation calls in a tight loop are
// wasted work at best and, in some hosts, enough to churn the tab's
// navigation state before the first one finishes. One caller does the work;
// the rest recognize the session is already gone and skip it.
//
// `expectedAccountId` closes the LAST window for the stale-session race the
// rest of this module already guards against: refreshSession() can decide
// "not stale" (or reject with a genuine, non-stale failure) at one instant,
// but the calling interceptor's own catch block — and this function — only
// run after that promise settles, one or more microtask ticks later. A
// replacement login can land in exactly that gap. Re-checking here, as late
// as possible and with no further await between the read and the clear,
// shrinks that window to nothing: pass the account this refresh attempt was
// FOR (decoded from the interceptor's own pre-refresh `stored` token) and
// skip clearing entirely if storage no longer shows that account — a
// newer, active session is not this failure's business. userId alone isn't
// enough, same lesson as the in-flight drift checks: a logout-and-log-back-in
// for the SAME account preserves the userId claim but rotates the refresh
// token, so it also needs `expectedRefreshToken` (the interceptor's own
// pre-refresh `stored.refreshToken`) to tell "the account this failure was
// for" apart from "a brand new session for that same account."
//
// Returns whether this failure's session is gone (cleared here, or already
// cleared by a sibling), as opposed to skipped because a newer session is
// active. The request interceptor only rejects with SESSION_EXPIRED_MESSAGE in
// the first case: paymentErrors.isUnauthorized keys the login-screen payment
// notice on it, and a newer session's login must not inherit that notice.
function logOutAndRedirect(expectedAccountId, expectedRefreshToken) {
  const stored = readCurrentUser();
  if (!stored) {
    return true;
  }
  if (
    expectedAccountId &&
    (decodeJwtPayload(stored.token)?.userId !== expectedAccountId || stored.refreshToken !== expectedRefreshToken)
  ) {
    return false;
  }
  clearSession();
  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
  return true;
}

// True only when this specific request is the one this guard is about: it
// carries a Bearer token for the SAME ACCOUNT currently signed in. A public
// call (login, country-codes, ...) never sets that header at all. This
// compares the `userId` claim, not the token string itself — several
// requests can be in flight together at mount (AuthContext's own verify call
// among them), each built from whatever was in storage a moment earlier, and
// an exact-string comparison against a freshly re-read `stored.token` would
// wrongly reject any request built just before a concurrent refresh (fired
// by a sibling request) already rewrote storage — the exact race this
// interceptor exists to handle, not one it should be defeated by. The
// account (userId) is what must not change out from under an in-flight
// request; the token value changing IS the point.  Most call sites send
// `Bearer ${token}`; a couple send the bare token with no prefix.
function requestCarriesStoredToken(config, stored) {
  const authHeader = config.headers?.get?.("Authorization");
  const bearerToken = authHeader?.replace(/^Bearer\s+/, "");
  if (!bearerToken || !stored?.token) {
    return false;
  }
  const requestUserId = decodeJwtPayload(bearerToken)?.userId;
  const storedUserId = decodeJwtPayload(stored.token)?.userId;
  return Boolean(requestUserId) && requestUserId === storedUserId;
}

// Rewrites this request's own token to `token` — the header, and, for the
// one call site that needs it, the body. verifyJwtToken (POST
// /users/verify-jwttoken) sends `{ jwttoken }` and the backend controller
// reads it from req.body, not the Authorization header at all — confirmed by
// reading it live: this interceptor was updating the header correctly while
// the body still carried the pre-refresh token, and the server 403'd on the
// body value alone. No other call site embeds the token in its body (grepped
// `common.services.js`/`auth.services.js`), so this is a named exception,
// not a generic body-scanning rule.
function syncRequestToken(config, token) {
  config.headers.set("Authorization", `Bearer ${token}`);
  if (config.data && typeof config.data === "object" && "jwttoken" in config.data) {
    config.data.jwttoken = token;
  }
}

axios.interceptors.request.use(async (config) => {
  if (config.url === REFRESH_URL) {
    return config;
  }

  const stored = readCurrentUser();
  if (!requestCarriesStoredToken(config, stored)) {
    return config;
  }

  if (isTokenExpired(stored.token)) {
    try {
      const updated = await refreshSession();
      syncRequestToken(config, updated.token);
      return config;
    } catch (err) {
      // A stale refresh means a different (possibly perfectly valid)
      // session has since taken over — that account is not this guard's
      // business, so it must not be logged out over a request that was
      // never its.
      if (
        !(err instanceof StaleRefreshError) &&
        logOutAndRedirect(decodeJwtPayload(stored.token)?.userId, stored.refreshToken)
      ) {
        return Promise.reject(new Error(SESSION_EXPIRED_MESSAGE));
      }
      return Promise.reject(new Error("Session changed"));
    }
  }

  // Storage holds a valid token, but this request built its own header (and,
  // for verifyJwtToken, body) from an earlier, since-superseded one — a
  // sibling request's refresh already landed while this one was in flight to
  // the interceptor. Sync it rather than sending the now-stale value: two
  // requests can start together at mount (AuthContext's own verify call
  // among them), each reading storage a moment apart, and only one of them
  // needs to actually trigger the refresh.
  const authHeader = config.headers?.get?.("Authorization");
  const bearerToken = authHeader?.replace(/^Bearer\s+/, "");
  if (bearerToken !== stored.token) {
    syncRequestToken(config, stored.token);
  }

  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const status = error.response?.status;

    if (!config || config.url === REFRESH_URL || status !== 401 || config._refreshRetried) {
      return Promise.reject(error);
    }

    const stored = readCurrentUser();
    if (!requestCarriesStoredToken(config, stored)) {
      return Promise.reject(error);
    }

    try {
      const updated = await refreshSession();
      config._refreshRetried = true;
      syncRequestToken(config, updated.token);
      return axios(config);
    } catch (err) {
      // A stale refresh (the account changed underneath this request) must
      // not log out whatever new session has since taken over — same
      // reasoning as the request interceptor's own catch.
      if (!(err instanceof StaleRefreshError)) {
        // Left deliberately un-redirected-away-from twice: logOutAndRedirect
        // already ran once if the proactive check caught this same expiry.
        // Calling it again here is harmless (clearSession and a same-page
        // navigate are both idempotent) and is the only path for the case the
        // proactive check never saw at all — a token revoked server-side after
        // this request was already sent. Rejecting with the ORIGINAL error,
        // not the refresh call's own, keeps the ~20 existing per-component
        // "if 401, clear storage and navigate" handlers seeing the same error
        // shape they already handle; this interceptor running first just means
        // the session is already cleared by the time they do.
        logOutAndRedirect(decodeJwtPayload(stored.token)?.userId, stored.refreshToken);
      }
      return Promise.reject(error);
    }
  }
);

// #87 / SEC-085: the socket's way into the refresh above. socket.js
// dispatches SESSION_REFRESH_REQUESTED_EVENT when the server refuses the
// handshake ('unauthorized') or ends the connection at token expiry. This
// runs the SAME single-flight, cross-tab-locked refreshSession() the
// interceptors use, so a socket and a burst of HTTP 401s can never present
// the refresh token twice. Success is announced by announceRefreshed(); a
// genuine failure takes the normal forced-logout path. A stale failure (a
// different session took over meanwhile) is not this handler's business.
function handleRefreshRequest() {
  const stored = readCurrentUser();
  if (!stored?.token) {
    return;
  }
  refreshSession().catch((err) => {
    if (!(err instanceof StaleRefreshError)) {
      logOutAndRedirect(decodeJwtPayload(stored.token)?.userId, stored.refreshToken);
    }
  });
}

if (typeof window !== "undefined") {
  window.addEventListener(SESSION_REFRESH_REQUESTED_EVENT, handleRefreshRequest);
}
